import { FileUp, TriangleAlert } from 'lucide-react'
import { useId, useState } from 'react'
import { toast } from 'sonner'

import type { AppStatus } from '@/shared/api/contracts'
import { isApiError } from '@/shared/api/errors'
import { errorMessage } from '@/shared/components/data'
import { Button, Textarea } from '@/shared/components/ui'
import { useT } from '@/shared/i18n'

import { useManualStructure, useUploadManual } from '../api'

export interface ManualFormProps {
  app: AppStatus
  /** Sin manual la IA no tiene con qué resolver: el asistente no sigue sin él. */
  required?: boolean
  onSaved: (estado: AppStatus) => void
}

function palabrasDe(texto: string): number {
  return texto.split(/\s+/).filter(Boolean).length
}

/**
 * Cargar el manual técnico de una app: lo que la IA lee para resolver.
 *
 * Muestra la estructura que pide siete-soporte (las secciones y para qué sirve
 * cada una) y quien lo carga puede partir de la plantilla, pegar el texto o
 * subir un `.md`. La validación es la de siete-soporte: si algo falta, se
 * muestra la lista tal como vino, al lado del texto, para corregirlo ahí.
 */
export function ManualForm({ app, required = false, onSaved }: ManualFormProps) {
  const t = useT()
  const estructura = useManualStructure()
  const subir = useUploadManual()
  const [contenido, setContenido] = useState('')
  const [faltan, setFaltan] = useState<string[]>([])
  const textareaId = useId()

  const minimo = estructura.data?.minimo_palabras ?? 0
  const palabras = palabrasDe(contenido)

  const leerArchivo = async (archivo: File | undefined) => {
    if (!archivo) return
    setContenido(await archivo.text())
    setFaltan([])
  }

  return (
    <form
      aria-label={t('support.apps.manual.title', { app: app.name })}
      className="flex flex-col gap-3"
      onSubmit={(event) => {
        event.preventDefault()
        if (!contenido.trim()) return
        subir.mutate(
          { appId: app.id, contenido },
          {
            onSuccess: (estado) => {
              setFaltan([])
              toast.success(t('support.apps.manual.saved', { app: app.name }))
              onSaved(estado)
            },
            onError: (error) => {
              const detalle = isApiError(error) ? error.details.faltan : null
              setFaltan(
                Array.isArray(detalle)
                  ? detalle.map(String)
                  : [errorMessage(error, t('support.apps.manual.error'))],
              )
            },
          },
        )
      }}
    >
      {required ? (
        <div
          role="alert"
          className="hairline flex gap-2 rounded-[var(--radius-card)] bg-surface-2/60 p-3 text-xs text-text-1"
        >
          <TriangleAlert aria-hidden="true" size={14} className="mt-0.5 shrink-0 text-overdue" />
          <p>{t('support.apps.manual.required', { app: app.name })}</p>
        </div>
      ) : null}

      <section className="flex flex-col gap-1.5">
        <h3 className="text-2xs tracking-wide text-text-3 uppercase">
          {t('support.apps.manual.structure')}
        </h3>
        <ul className="flex flex-col gap-1 text-xs">
          {(estructura.data?.secciones ?? []).map((seccion) => (
            <li key={seccion.titulo}>
              <span className="num text-text-1">{seccion.titulo}</span>
              <span className="text-text-3"> — {seccion.para_que}</span>
            </li>
          ))}
        </ul>
        {estructura.data ? (
          <p className="text-2xs text-text-3">
            {t('support.apps.manual.limits', {
              minimo,
              maximo: estructura.data.maximo_caracteres.toLocaleString(),
            })}
          </p>
        ) : null}
      </section>

      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          disabled={!estructura.data}
          onClick={() => {
            setContenido(estructura.data?.plantilla_md ?? '')
            setFaltan([])
          }}
        >
          {t('support.apps.manual.useTemplate')}
        </Button>
        <label className="inline-flex cursor-pointer items-center gap-1.5 text-xs text-text-2 hover:text-text-1">
          <FileUp aria-hidden="true" size={13} />
          {t('support.apps.manual.upload')}
          <input
            type="file"
            accept=".md,.markdown,.txt,text/markdown,text/plain"
            className="sr-only"
            onChange={(event) => void leerArchivo(event.target.files?.[0])}
          />
        </label>
      </div>

      <label htmlFor={textareaId} className="sr-only">
        {t('support.apps.manual.label', { app: app.name })}
      </label>
      <Textarea
        id={textareaId}
        value={contenido}
        rows={14}
        placeholder={t('support.apps.manual.placeholder')}
        onChange={(event) => {
          setContenido(event.target.value)
          if (faltan.length > 0) setFaltan([])
        }}
        className="num text-xs"
      />
      <p className="num text-2xs text-text-3">
        {t('support.apps.manual.words', { count: palabras, minimo })}
      </p>

      {faltan.length > 0 ? (
        <div role="alert" className="flex flex-col gap-1 text-xs">
          <span className="text-overdue">{t('support.apps.manual.missing')}</span>
          <ul className="list-disc pl-5 text-text-1">
            {faltan.map((falta) => (
              <li key={falta}>{falta}</li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="flex justify-end">
        <Button type="submit" variant="primary" disabled={subir.isPending || !contenido.trim()}>
          {t('support.apps.manual.save')}
        </Button>
      </div>
    </form>
  )
}
